import { TDynamicFormConfig } from "@/features/Edit/dynamic-edit-form";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { Control } from "react-hook-form";

import { AutocompleteAsyncField } from "@/features/FormCard/Fields";
import { z } from "zod";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";

/**
 * Prevent empty strings from being transformed into 0
 */
const nullableNumber = z.preprocess(
  (value) => (value === "" || value === undefined ? null : value),
  z.coerce.number().nullable().optional(),
);

// Example of a dynamic form config
export const dynamicFormConfig = {
  id: z.number(),
  name: z
    .string()
    .min(1, "Le nom est obligatoire")
    .transform((val) => val.toUpperCase()),
  isin: z
    .string()
    .refine(
      (val) => val === "" || val === null || val.length === 12,
      "L'ISIN doit comporter  12 caractères",
    )
    .nullable()
    .optional(),
  code: z.string().nullable().optional(),
  ticker: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  issuer_id: z.coerce.number().optional().nullable(),
  n1_issuer_name: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        // we recv / send as string the data
        z.string().nullable().optional(),

        // for the autocomplete, the form state is an object
        z.object({
          text: z.string(),
          id: z.number(),
        }),
      ]),
    )
    .optional(),
  sector: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  product: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  maturity_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
  coupon_rate: nullableNumber,
  commitment: nullableNumber,
  capital_callable: z.boolean().optional().nullable(),
  stock_option_strike: nullableNumber,
  transfer_code: z.string().nullable().optional(),
  lpt_liquidity: z.string().nullable().optional(),
  lpt_fund_vintage_start_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
  lpt_investment_period_end_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
  lpt_investment_duration: z.coerce.number().optional().nullable(),
  lpt_net_percentage_capital_called: z.coerce.number().optional().nullable(),
  lpt_loan_to_value: z.coerce.number().optional().nullable(),
  lpt_expected_distribution_rate: z.coerce.number().optional().nullable(),
  lpt_expected_investment_return: z.coerce.number().optional().nullable(),
  lpt_expected_annual_fee_rate: z.coerce.number().optional().nullable(),
  // n1_issuer_name: z.string().nullable().optional(),
  strategy_id: z.coerce.number().optional().nullable(),
  asset_class_1: z.string().nullable().optional(),
  asset_class_2: z.string().nullable().optional(),
};

const DynamicFormSchema = z.object(dynamicFormConfig);
export const dynamicFormFields = DynamicFormSchema.keyof().enum;

export const dynamicNewFormConfigWithoutId: Omit<TDynamicFormConfig, "id"> =
  (() => {
    const { id, ...configWithoutId } = dynamicFormConfig;
    return configWithoutId;
  })();

function ConcentratedAssetField(control: Control<any>) {
  return (
    <AutocompleteAsyncField
      name="concentrated_asset"
      label="Actif concentré du client"
      control={control}
      base_url={`${Clients.N1}/${Tables.N1_PORTFOLIOS}`}
      id_column="n1_portfolio_id"
      column_name={dynamicFormFields.name}
    />
  );
}

export const concentrated_asset_conf = {
  label: "Actif concentré du client",
  validator: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        // we recv / send as string the data
        z.string(),

        // for the autocomplete, the form state is an object
        z.object({
          text: z.string(),
          id: z.number(),
          account_type: z.string(),
          currency: z.string(),
        }),
      ]),
    )
    .optional(),
  field: ConcentratedAssetField,
};
